import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { handleCheckoutClick } from '../utils/metaPixel';
import { ShieldCheckIcon, LockIcon, BrainIcon, HeartPulseIcon, AlertCircleIcon, TimerIcon, SunIcon, BookOpenIcon } from 'lucide-react';
function AnimatedNumber({
  target,
  duration = 1.5
}: {
  target: number;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const increment = target / (duration * 60);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [isInView, target, duration]);
  return <span ref={ref} data-id="element-94">{count.toLocaleString()}</span>;
}
export function MoneySection() {
  const modules = [{
    title: 'The Hidden Crisis',
    desc: 'Why women are chronically overstimulated',
    icon: AlertCircleIcon
  }, {
    title: 'Your Nervous System',
    desc: 'Simple science, zero overwhelm',
    icon: BrainIcon
  }, {
    title: 'Signs & Symptoms',
    desc: 'What your body has been trying to tell you',
    icon: HeartPulseIcon
  }, {
    title: 'The 5-Minute Reset',
    desc: 'Your daily nervous system rescue framework',
    icon: TimerIcon
  }, {
    title: 'Daily Rhythm & Environment',
    desc: 'Design your life to support regulation',
    icon: SunIcon
  }, {
    title: 'Boundaries, Case Studies & Action Plan',
    desc: 'Real tools, real results, a real path forward',
    icon: BookOpenIcon
  }];
  return <section className="py-12 px-5 bg-sand-light" data-id="element-95">
      <div className="max-w-xl mx-auto" data-id="element-96">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-8" data-id="element-97">
          <h2 className="font-serif text-4xl md:text-5xl text-charcoal mb-3" data-id="element-98">
            What You Get Today
          </h2>
          <p className="text-base text-charcoal-light" data-id="element-99">
            Inside The Nervous System Reset for Overstimulated Women, you'll
            receive:
          </p>
        </motion.div>

        <motion.div id="money-section" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-white rounded-2xl p-6 shadow-md border border-sand overflow-hidden scroll-mt-16" data-id="element-100">
          {/* Module list */}
          <div className="space-y-0 divide-y divide-sand/60" data-id="element-101">
            {modules.map((mod, idx) => {
            const Icon = mod.icon;
            return <motion.div key={idx} className="flex gap-3.5 py-4 first:pt-0 last:pb-0" initial={{
              opacity: 0,
              x: -12
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: idx * 0.08,
              duration: 0.4
            }} data-id="element-102">
                  <div className="w-9 h-9 rounded-lg bg-sage/15 flex items-center justify-center shrink-0" data-id="element-103">
                    <Icon className="w-[18px] h-[18px] text-sage-dark" data-id="element-104" />
                  </div>
                  <div data-id="element-105">
                    <h4 className="font-medium text-charcoal text-sm leading-snug" data-id="element-106">
                      {mod.title}
                    </h4>
                    <p className="text-xs text-charcoal-light mt-0.5" data-id="element-107">
                      {mod.desc}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>

          {/* Divider */}
          <div className="my-6 border-t border-sand" data-id="element-108" />

          {/* CTA */}
          <motion.button type="button" onClick={handleCheckoutClick} whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} className="w-full bg-terracotta hover:bg-terracotta-dark text-white font-semibold text-lg py-4 px-6 rounded-full shadow-lg hover:shadow-xl transition-all mb-4 cursor-pointer" data-id="element-109">
            Get Instant Access Now
          </motion.button>

          {/* Pricing row */}
          <div className="flex items-center justify-center gap-3 mb-3" data-id="element-110">
            <span className="line-through text-red-400 text-base font-medium" data-id="element-111">
              $19.99
            </span>
            <span className="font-serif text-5xl text-sage-dark font-bold tracking-tight" data-id="element-112">
              $6.39
            </span>
            <span className="bg-terracotta/10 text-terracotta-dark font-bold px-3 py-1 rounded-full text-xs flex items-center gap-1" data-id="element-113">
              <span className="w-1.5 h-1.5 rounded-full bg-terracotta animate-pulse" data-id="element-114"></span>
              68% OFF
            </span>
          </div>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-4 text-[11px] text-charcoal-light mb-5" data-id="element-115">
            <span className="flex items-center gap-1" data-id="element-116">
              <LockIcon className="w-3 h-3" data-id="element-117" /> Secure payment
            </span>
            <span className="flex items-center gap-1" data-id="element-118">
              <ShieldCheckIcon className="w-3 h-3" data-id="element-119" /> Instant access
            </span>
          </div>

          {/* Social proof */}
          <div className="pt-5 border-t border-sand" data-id="element-120">
            <p className="font-medium text-charcoal text-center text-sm mb-4" data-id="element-121">
              More than <AnimatedNumber target={12000} data-id="element-122" /> women have used these
              reset methods.
            </p>
            <ul className="space-y-2 text-xs text-charcoal-light" data-id="element-123">
              <li className="flex items-center gap-2" data-id="element-124">
                <span className="text-sage" data-id="element-125">•</span> 87% feel calmer within 3 to
                5 minutes
              </li>
              <li className="flex items-center gap-2" data-id="element-126">
                <span className="text-sage" data-id="element-127">•</span> 72% report reduced mental
                noise within days
              </li>
            </ul>
            <div className="mt-4 bg-cream p-3 rounded-xl italic text-xs text-charcoal text-center shadow-sm" data-id="element-128">
              "This is the first time my body actually relaxed"
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}